import { useMemo } from 'react';
import {
  FilterToolbarGroup,
  FilterToolbarItemId,
} from 'src/components/Filters';
import { useContent } from 'src/contexts/ContentContext';
import { PosSelect } from 'src/core/POS/PosSelect';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import { ContentId } from 'src/utils/constants/contentId';
import { ActionOutboxEntityType } from 'src/WebApiController';

import { TagValueFilterInput } from './TagValueFilterInput';

export const useTagFiltersBase = (
  tagName: string | null,
  tagValue: string | null,
  setTagName: (tagName: string | null) => void,
  setTagValue: (tagValue: string | null) => void,
  titleContentId: ContentId,
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Purchase
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.SellerEvent,
  includeInReport?: boolean,
  disabled?: boolean,
  tagNameFilterId = 'tagName',
  tagValueFilterId = 'tagValue'
): FilterToolbarGroup => {
  const { tagsMetadata: existingTagsMetadata = [] } = useTagsForEntityType(
    disabled ? undefined : entityType,
    includeInReport
  );

  const noTagsMsg = useContent(ContentId.NoTagsExist);

  const selectedTagMetadata = useMemo(() => {
    return (existingTagsMetadata ?? []).find((r) => r.key === tagName);
  }, [existingTagsMetadata, tagName]);

  const keyOptionsContent = useMemo(() => {
    const keyOptions = (existingTagsMetadata ?? [])
      .toSorted((r1, r2) => r1.key.localeCompare(r2.key))
      .reduce(
        (re, r) => {
          re[r.key] = r.key;
          return re;
        },
        {} as Record<string, string>
      );

    return keyOptions ?? {};
  }, [existingTagsMetadata]);

  const noTagsExist = useMemo(() => {
    return (existingTagsMetadata ?? []).length === 0;
  }, [existingTagsMetadata]);

  return {
    titleContentId,
    type: 'row',
    items: [
      {
        filterId: tagNameFilterId as FilterToolbarItemId,
        labelContentId: ContentId.Name,
        filterQueryKeys: [tagNameFilterId] as FilterToolbarItemId[],
        filterItem: (
          <PosSelect
            style={{ width: '100%' }}
            onChange={(newValue: string) => {
              if (tagName !== newValue) {
                setTagName(newValue);
              }
            }}
            value={tagName ?? ''}
            displayText={noTagsExist ? noTagsMsg : (tagName ?? '')}
            searchable
            disabled={noTagsExist}
            valueOptionsContent={keyOptionsContent}
            placeholderText={ContentId.None}
            enableEmptySelection
            shape="rectFull"
          />
        ),
      },
      {
        filterId: tagValueFilterId as FilterToolbarItemId,
        labelContentId: ContentId.Value,
        filterQueryKeys: [tagValueFilterId] as FilterToolbarItemId[],
        filterItem: (
          <TagValueFilterInput
            valueType={selectedTagMetadata?.valueType}
            disabled={selectedTagMetadata == null || noTagsExist}
            value={tagValue ?? ''}
            onChange={(newValue) => {
              if (tagValue !== newValue) {
                setTagValue(newValue);
              }
            }}
          />
        ),
      },
    ].filter((f) => f!),
  };
};
